import { useCallback, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useUpdateReservationStatus } from "@/hooks/useReservations";
import { CheckinStatus, CheckoutStatus } from "@/types/reservation";
import { getCurrentReservationPath } from "../utils/getCurrentReservationPath";

export function useReservationStatus(
  checkinStatus?: CheckinStatus,
  checkoutStatus?: CheckoutStatus,
) {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const updateReservationMutation = useUpdateReservationStatus();

  const detailsBasePath = useMemo(() => {
    return getCurrentReservationPath(location.pathname);
  }, [location.pathname]);

  const handleCheckInStatusChange = useCallback(
    async (reservationId: string, newStatus: CheckinStatus) => {
      if (!newStatus) return;

      try {
        await updateReservationMutation.mutateAsync({
          id: reservationId,
          checkinStatus: newStatus,
        });

        navigate(detailsBasePath);
      } catch (error) {
        console.error("Failed to update reservation status:", error);
      }
    },
    [detailsBasePath, navigate, updateReservationMutation],
  );

  const handleCheckOutStatusChange = useCallback(
    async (reservationId: string, newStatus: CheckoutStatus) => {
      if (!newStatus) return;

      try {
        await updateReservationMutation.mutateAsync({
          id: reservationId,
          checkoutStatus: newStatus,
        });

        navigate(detailsBasePath);
      } catch (error) {
        console.error("Failed to update reservation status:", error);
      }
    },
    [detailsBasePath, navigate, updateReservationMutation],
  );

  const statusConfig = useMemo(() => {
    if (detailsBasePath === "/arrivals") {
      return {
        statuses: [
          {
            status: CheckinStatus.DONE,
            color: "border-accent-green text-accent-green",
            text: "Confirm",
          },
          {
            status: CheckinStatus.NOT_DONE,
            color: "border-gray-5 text-gray-5",
            text: "Check-in",
          },
        ],
        currentStatus: checkinStatus,
        onStatusChange: (newStatus: unknown) =>
          handleCheckInStatusChange(id!, newStatus as CheckinStatus),
      };
    }

    if (detailsBasePath === "/departures") {
      return {
        statuses: [
          {
            status: CheckoutStatus.DONE,
            color: "border-accent-green text-accent-green",
            text: "Confirm",
          },
          {
            status: CheckoutStatus.NOT_DONE,
            color: "border-gray-5 text-gray-5",
            text: "Check-out",
          },
        ],
        currentStatus: checkoutStatus,
        onStatusChange: (newStatus: unknown) =>
          handleCheckOutStatusChange(id!, newStatus as CheckoutStatus),
      };
    }

    return null;
  }, [
    detailsBasePath,
    checkinStatus,
    handleCheckInStatusChange,
    id,
    checkoutStatus,
    handleCheckOutStatusChange,
  ]);

  return { detailsBasePath, statusConfig };
}
